import React from 'react';
import { Box, ButtonBase, CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Company, StatusColor, Snapshot } from './lib/types';
import { STATUS_COLORS, STATUS_LABEL } from './lib/selectors';
import { ageMinutes, fmtAge, fmtIct } from './lib/ict';
import { useNow } from './hooks/useNow';
import { StatusDot } from './StatusDot';
import { ACCENT, BAD_TEXT_ON_DARK, BURGUNDY, FONT, GOLD_DARK, GOOD_TEXT_ON_DARK, RADIUS, TONE, WARN_TEXT, accentText } from './lib/brand';
import { ink } from './lib/tone';

export { ACCENT } from './lib/brand';

export function CompanyToggle({ company, names, onChange }: { company: Company; names: Record<Company, string>; onChange: (c: Company) => void }) {
  return (
    <ToggleButtonGroup
      exclusive
      value={company}
      onChange={(_e, v: Company | null) => { if (v) onChange(v); }}
      aria-label="Company"
      role="tablist"
      sx={{ bgcolor: 'action.hover', borderRadius: RADIUS.pill, p: 0.4, '& .MuiToggleButton-root': { border: 0, borderRadius: RADIUS.pill, px: 2.2, py: 0.6, textTransform: 'none', fontWeight: 600, gap: 1, color: 'text.secondary' } }}
    >
      {(['lifescribe', 'quill'] as Company[]).map((c) => (
        <ToggleButton
          key={c}
          value={c}
          role="tab"
          aria-selected={company === c}
          aria-pressed={undefined}
          sx={{ '&.Mui-selected, &.Mui-selected:hover': { bgcolor: ACCENT[c].acc, color: TONE.strong.fg } }}
        >
          <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: company === c ? TONE.strong.fg : ACCENT[c].acc }} />
          {names[c]}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/** The three status meanings, each a button that filters the bot table to that colour. */
export function StatusLegend({ active, onPick }: { active: StatusColor | null; onPick: (c: StatusColor | null) => void }) {
  const items: { c: StatusColor; text: string }[] = [
    { c: 'green', text: 'Active (worked in last 6h)' },
    { c: 'yellow', text: 'Pending decision (awaiting Grant / exec disagreement)' },
    { c: 'red', text: 'Idle' },
  ];
  return (
    <Box role="group" aria-label="Status legend" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {items.map(({ c, text }) => (
        <ButtonBase
          key={c}
          aria-pressed={active === c}
          onClick={() => onPick(active === c ? null : c)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7, fontSize: 12, color: 'text.secondary', borderRadius: RADIUS.md, px: 0.6, py: 0.2, outline: active === c ? `2px solid ${STATUS_COLORS[c]}` : 'none' }}
        >
          <StatusDot color={c} size={9} label={STATUS_LABEL[c]} />
          {text}
        </ButtonBase>
      ))}
    </Box>
  );
}

/**
 * "Synced 12 s ago · data 3 min old". Green while the snapshot is under 10 min
 * old, amber from 10 min, red from 70 min or when the last poll failed (the last
 * good data stays on screen either way).
 */
export function SyncIndicator({ lastSyncedAt, generatedIct, polling, failed }: { lastSyncedAt: number | null; generatedIct?: string | null; polling: boolean; failed: boolean }) {
  const now = useNow(1000);
  const age = ageMinutes(generatedIct, now);
  const syncedSec = lastSyncedAt ? Math.max(0, Math.round((now - lastSyncedAt) / 1000)) : null;
  const level: 'ok' | 'warn' | 'bad' = failed || (age != null && age >= 70) ? 'bad' : age != null && age >= 10 ? 'warn' : 'ok';
  const color = { ok: STATUS_COLORS.green, warn: GOLD_DARK, bad: BURGUNDY }[level];
  // The dot and border keep the status colour; the words use a readable shade of it (green text was 3:1 on cream).
  const text = { ok: ink(TONE.good.fg, GOOD_TEXT_ON_DARK), warn: WARN_TEXT, bad: ink(BURGUNDY, BAD_TEXT_ON_DARK) }[level];
  const label = failed
    ? 'Sync error · retrying'
    : `Synced ${syncedSec == null ? '…' : `${syncedSec}s ago`} · data ${age == null ? 'no data' : age < 1 ? '<1 min' : fmtAge(age)} old`;
  return (
    <Tooltip title={`Snapshot ${fmtIct(generatedIct)}. Green under 10 min, amber from 10 min, red from 70 min or on a sync error.`}>
      <Box
        data-testid="sync-indicator"
        data-level={level}
        role="status"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.9, border: '1px solid', borderColor: color, color: text, borderRadius: RADIUS.pill, px: 1.4, py: 0.4, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
      >
        {polling ? <CircularProgress size={11} thickness={6} sx={{ color }} /> : <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />}
        {label}
      </Box>
    </Tooltip>
  );
}

export function HeaderBar({
  snapshot, company, onCompany, statusFilter, onStatus, sync, syncControl,
}: {
  snapshot: Snapshot;
  company: Company;
  onCompany: (c: Company) => void;
  statusFilter: StatusColor | null;
  onStatus: (c: StatusColor | null) => void;
  sync: { lastSyncedAt: number | null; polling: boolean; failed: boolean };
  /** The Sync now button and its tracker (super admin only), shown beside the indicator. */
  syncControl?: React.ReactNode;
}) {
  const names = { lifescribe: snapshot.companies.lifescribe.name, quill: snapshot.companies.quill.name } as Record<Company, string>;
  const acc = ACCENT[company].acc;
  return (
    <Box component="header" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '1 1 320px', minWidth: 0 }}>
        <Box aria-hidden sx={{ width: 40, height: 40, borderRadius: RADIUS.lg, border: `2px solid ${acc}`, color: accentText(acc), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>GS</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={{ fontFamily: FONT.display, fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: 'primary.main' }}>Bot Org &amp; Operations</Typography>
          <Typography variant="caption" color="text.secondary">Grant Schick · C-suite → Coordinator → bots · every item links to its source file</Typography>
        </Box>
      </Box>
      <CompanyToggle company={company} names={names} onChange={onCompany} />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 0.8, flex: '1 1 320px' }}>
        <StatusLegend active={statusFilter} onPick={onStatus} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          <SyncIndicator lastSyncedAt={sync.lastSyncedAt} generatedIct={snapshot.generated_ict} polling={sync.polling} failed={sync.failed} />
          {syncControl}
        </Box>
      </Box>
    </Box>
  );
}
